/**
 * Policy Acknowledgements Page
 * Staff section → Policy Acknowledgements (owner only)
 * 5 slots + 2 built-in draggable templates
 */
import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import AppShell from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  Upload, Mail, Trash2, FileText, Loader2, ChevronDown,
  CheckCircle2, Clock, Printer, Download, X, Users, Plus, GripVertical, Save, RefreshCw
} from "lucide-react";
import { POLICY_TEMPLATES } from "@/components/policy/PolicyTemplateViewer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const APP_URL = window.location.origin;

const SLOT_DEFAULTS = [
  "Shaken Baby / Abuse / Head Trauma Policy",
  "Emergency Preparedness Training Review",
  "Custom Policy 2",
  "Custom Policy 3",
  "Custom Policy 4",
];

const BUILT_IN_TEMPLATES = [
  {
    key: "shaken_baby",
    label: "Shaken Baby / Abuse / Head Trauma Policy",
    description: "Center policy on identifying, preventing, and reporting SBS, abusive head trauma, and child maltreatment.",
    color: "#dc2626",
    bg: "#fef2f2",
    border: "#fecaca",
  },
  {
    key: "emergency_procedures",
    label: "Emergency Preparedness (EPR) — Staff Training Review",
    description: "Summary of all emergency procedures staff must know: evacuation, lockdown, fire, medical, missing child, and more.",
    color: "#1a365d",
    bg: "#eff6ff",
    border: "#bfdbfe",
  },
  {
    key: "disclosure_statement",
    label: "Disclosure Statement",
    description: "Application for Child Care Employment including provisional employment, clearance affirmations, and disqualifying offense acknowledgement.",
    color: "#7c3aed",
    bg: "#f5f3ff",
    border: "#ddd6fe",
  },
];

export default function PolicyAcknowledgementsPage() {
  const { organization } = useAuth();
  const orgName = organization?.name || "";
  const [searchParams] = useSearchParams();
  const [docs, setDocs] = useState(Array(5).fill(null));
  const [slotLabels, setSlotLabels] = useState([...SLOT_DEFAULTS]);
  const [uploading, setUploading] = useState(Array(5).fill(false));
  const [dragOver, setDragOver] = useState(Array(5).fill(false));
  const [templateDragKey, setTemplateDragKey] = useState(null);
  const [slotTemplateOver, setSlotTemplateOver] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null); // templateKey to preview in modal
  const [staffList, setStaffList] = useState([]);
  const [showSend, setShowSend] = useState(null);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [sendNote, setSendNote] = useState("");
  const [sending, setSending] = useState(false);
  const [reports, setReports] = useState([]);
  const [reportDocId, setReportDocId] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [disclosureStateCode, setDisclosureStateCode] = useState(() => localStorage.getItem("disclosure_state_code") || "");
  const [savingStateCode, setSavingStateCode] = useState(false);
  const fileRefs = useRef(Array(5).fill(null).map(() => ({ current: null })));

  const [resending, setResending] = useState(null); // record id being resent
  const [backfillingPdfs, setBackfillingPdfs] = useState(false);

  useEffect(() => {
    fetchDocs();
    fetchStaff();
    fetchReport();

    // Auto-refresh the report every 30 seconds so acknowledged status updates without manual refresh
    const interval = setInterval(() => fetchReport(reportDocId || null), 30000);
    return () => clearInterval(interval);
  }, []);

  // Auto-open send panel if ?slot= param is present (from compliance area button)
  useEffect(() => {
    const slotParam = searchParams.get("slot");
    if (slotParam !== null) {
      const idx = parseInt(slotParam);
      if (!isNaN(idx) && idx >= 0 && idx < 5) {
        setShowSend(idx);
        // Scroll to the slot smoothly
        setTimeout(() => {
          const el = document.getElementById(`policy-slot-${idx}`);
          if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 500);
      }
    }
  }, [searchParams]);

  const fetchDocs = async () => {
    try {
      const res = await axios.get(`${API}/policy-ack/documents`);
      const arr = Array(5).fill(null);
      (res.data?.documents || []).forEach(d => { if (d.slot >= 0 && d.slot <= 4) arr[d.slot] = d; });
      setDocs(arr);
      const labels = [...SLOT_DEFAULTS];
      arr.forEach((d, i) => { if (d?.label) labels[i] = d.label; });
      setSlotLabels(labels);
      if (arr[2]?.stateCode) setDisclosureStateCode(arr[2].stateCode);
    } catch {}
  };

  const fetchStaff = async () => {
    try {
      const res = await axios.get(`${API}/staff-records`);
      const staff = Array.isArray(res.data) ? res.data : (res.data?.staff || res.data?.records || []);
      setStaffList(staff.filter(s => s.email).map(s => ({ name: s.name, email: s.email })));
    } catch {}
  };

  const fetchReport = async (docId = null) => {
    setLoadingReport(true);
    try {
      const url = docId ? `${API}/policy-ack/report?doc_id=${docId}` : `${API}/policy-ack/report`;
      const res = await axios.get(url);
      setReports(res.data?.records || []);
    } catch {} finally { setLoadingReport(false); }
  };

  const handleLoadTemplate = async (slot, templateKey) => {
    const tmpl = BUILT_IN_TEMPLATES.find(t => t.key === templateKey);
    try {
      const res = await axios.post(`${API}/policy-ack/template/${slot}`, {
        templateKey,
        label: tmpl?.label || SLOT_DEFAULTS[slot],
        stateCode: templateKey === "disclosure_statement" ? disclosureStateCode : "",
      });
      toast.success(`"${tmpl?.label}" loaded into slot ${slot + 1}`);
      fetchDocs();
    } catch {
      toast.error("Failed to load template");
    }
    setTemplateDragKey(null);
    setSlotTemplateOver(null);
  };

  const handleSaveStateCode = async () => {
    // Always persist to localStorage so it survives page reloads
    localStorage.setItem("disclosure_state_code", disclosureStateCode);
    const doc = docs[2];
    if (!doc?.isTemplate) {
      toast.success("State code saved");
      return;
    }
    setSavingStateCode(true);
    try {
      await axios.patch(`${API}/policy-ack/documents/${doc.id}/state-code`, { stateCode: disclosureStateCode });
      const arr = [...docs]; arr[2] = { ...arr[2], stateCode: disclosureStateCode }; setDocs(arr);
      toast.success("State code saved");
    } catch { toast.error("Failed to save"); } finally { setSavingStateCode(false); }
  };

  const handleUpload = async (slot, file) => {
    if (!file) return;
    const allowed = ['application/pdf', 'image/jpeg', 'image/png'];
    if (!allowed.includes(file.type)) { toast.error("PDF or image only"); return; }
    if (file.size > 15 * 1024 * 1024) { toast.error("File must be under 15MB"); return; }
    const up = [...uploading]; up[slot] = true; setUploading(up);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('label', slotLabels[slot]);
      const res = await axios.post(`${API}/policy-ack/documents/${slot}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const arr = [...docs]; arr[slot] = res.data; setDocs(arr);
      toast.success("Document uploaded");
    } catch { toast.error("Upload failed"); } finally { const up2 = [...uploading]; up2[slot] = false; setUploading(up2); }
  };

  const handleDelete = async (slot) => {
    const doc = docs[slot];
    if (!doc) return;
    try {
      await axios.delete(`${API}/policy-ack/documents/${doc.id}`);
      const arr = [...docs]; arr[slot] = null; setDocs(arr);
      // Reset label to default when document is removed
      const labels = [...slotLabels];
      labels[slot] = SLOT_DEFAULTS[slot] || `Custom Policy ${slot + 1}`;
      setSlotLabels(labels);
      toast.success("Removed");
      fetchReport();
    } catch { toast.error("Failed to remove"); }
  };

  const handleSend = async (slot) => {
    const doc = docs[slot];
    if (!doc) { toast.error("Upload a document first"); return; }
    if (selectedRecipients.length === 0) { toast.error("Select at least one recipient"); return; }
    setSending(true);
    try {
      const res = await axios.post(`${API}/policy-ack/send`, {
        docId: doc.id,
        recipients: selectedRecipients,
        note: sendNote.trim(),
        appUrl: APP_URL,
      });
      toast.success(res.data.message);
      setShowSend(null);
      setSelectedRecipients([]);
      setSendNote("");
      fetchReport();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send"); } finally { setSending(false); }
  };

  const toggleRecipient = (s) => setSelectedRecipients(prev =>
    prev.find(r => r.email === s.email) ? prev.filter(r => r.email !== s.email) : [...prev, s]
  );

  const handleDeleteRecord = async (recordId) => {
    try {
      await axios.delete(`${API}/policy-ack/records/${recordId}`);
      setReports(prev => prev.filter(r => r.id !== recordId));
      toast.success("Record deleted");
    } catch { toast.error("Failed to delete record"); }
  };

  const handleResendRecord = async (recordId) => {
    setResending(recordId);
    try {
      const res = await axios.post(`${API}/policy-ack/records/${recordId}/resend`, { appUrl: APP_URL });
      toast.success(res.data.message);
      fetchReport();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to resend"); }
    finally { setResending(null); }
  };

  const handleDownloadRecord = async (recordId, policyName, staffName) => {
    try {
      const res = await axios.get(`${API}/policy-ack/records/${recordId}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch (e) {
      const msg = e.response?.status === 404
        ? "No PDF available — this record was acknowledged before PDF storage was enabled."
        : "Failed to download PDF";
      toast.error(msg);
    }
  };

  const handleBackfillPdfs = async () => {
    setBackfillingPdfs(true);
    try {
      const res = await axios.post(`${API}/policy-ack/admin/backfill-receipts`, {});
      const { fixed, receipt_only, failed } = res.data;
      if (fixed > 0 || receipt_only > 0) {
        toast.success(`Done. ${fixed + receipt_only} record${fixed + receipt_only !== 1 ? 's' : ''} restored.${failed > 0 ? ` ${failed} failed — check logs.` : ''}`);
        fetchReport();
      } else if (failed > 0) {
        toast.error(`Backfill ran but ${failed} record(s) failed. Check logs.`);
      } else {
        toast.success("All records already have PDFs — nothing to restore.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Backfill failed");
    } finally {
      setBackfillingPdfs(false);
    }
  };

  const handlePrintReport = () => {
    const filtered = reportDocId ? reports.filter(r => r.docId === reportDocId) : reports;
    const rows = filtered.map(r => `
      <tr>
        <td>${r.policyName || ''}</td>
        <td>${r.staffName || ''}</td>
        <td>${r.staffEmail || ''}</td>
        <td>${r.sentAt ? new Date(r.sentAt).toLocaleDateString() : ''}</td>
        <td style="color:${r.acknowledged ? '#16a34a' : '#dc2626'};font-weight:bold">
          ${r.acknowledged ? '✓ Acknowledged' : '⏳ Pending'}
        </td>
        <td>${r.acknowledgedAt ? new Date(r.acknowledgedAt).toLocaleDateString() : '—'}</td>
      </tr>`).join('');
    const w = window.open('', '_blank');
    w.document.write(`<!DOCTYPE html><html><head><title>Policy Acknowledgement Report</title>
    <style>body{font-family:Arial,sans-serif;padding:30px;font-size:11px}
    h1{font-size:16px;color:#1a365d}
    table{width:100%;border-collapse:collapse;margin-top:12px}
    th{background:#1a365d;color:#fff;padding:7px;text-align:left;font-size:10px}
    td{padding:6px 7px;border-bottom:1px solid #ddd}
    @media print{body{padding:12px}}</style></head><body>
    <h1>Policy Acknowledgement Report</h1>
    <p style="color:#666;font-size:11px">Generated: ${new Date().toLocaleString()}</p>
    <table><thead><tr>
      <th>Policy</th><th>Staff Name</th><th>Email</th><th>Sent</th><th>Status</th><th>Acknowledged On</th>
    </tr></thead><tbody>${rows || '<tr><td colspan="6" style="text-align:center;color:#888">No records</td></tr>'}</tbody></table>
    <script>window.onload=()=>window.print()</script></body></html>`);
    w.document.close();
  };

  const acknowledged = reports.filter(r => r.acknowledged).length;
  const pending = reports.filter(r => !r.acknowledged).length;

  return (
    <AppShell pageTitle="Policy Acknowledgements">
      <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Policy Acknowledgements</h1>
          <p className="text-sm text-gray-500 mt-1">Upload policy documents, send magic-link emails to staff, and track who has acknowledged each policy.</p>
        </div>

        {/* Built-in Templates — each card sits directly below its matching slot */}
        <div className="space-y-4">
          {Array(5).fill(null).map((_, slot) => {
            const doc = docs[slot];
            const isUploading = uploading[slot];
            const isDragOver = dragOver[slot];
            const isOpen = showSend === slot;

            return (
              <div key={slot} id={`policy-slot-${slot}`}
                className={`bg-white border-2 rounded-xl overflow-hidden shadow-sm transition-all ${showSend === slot ? 'border-blue-400 ring-2 ring-blue-100' : slotTemplateOver === slot ? 'border-green-400 ring-2 ring-green-100' : 'border-gray-200'}`}
                onDragOver={e => { if (templateDragKey) { e.preventDefault(); setSlotTemplateOver(slot); } }}
                onDragLeave={() => setSlotTemplateOver(null)}
                onDrop={e => { e.preventDefault(); if (templateDragKey) handleLoadTemplate(slot, templateDragKey); }}
              >
                {/* Slot header */}
                <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 bg-blue-600`}>{slot + 1}</div>
                    <div className="flex-1 min-w-0">
                      <Input value={slotLabels[slot]} onChange={e => { const l = [...slotLabels]; l[slot] = e.target.value; setSlotLabels(l); }}
                        className="h-8 text-sm font-semibold border-0 bg-transparent focus:bg-white focus:border p-1 rounded" placeholder="Policy name..." />
                      {doc?.isTemplate && (
                        <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium mt-0.5">
                          <CheckCircle2 className="w-3 h-3" /> Built-in Template
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    {doc && (
                      <>
                        {/* View: open template preview for built-in, file view for uploads */}
                        <button
                          onClick={() => doc.isTemplate ? setPreviewTemplate(doc.templateKey) : window.open(`${API}/policy-ack/documents/${doc.id}/view?token=${localStorage.getItem('token')}`, '_blank')}
                          className="text-xs text-blue-600 hover:underline px-2">View</button>
                        <Button size="sm" variant="outline" onClick={() => { setShowSend(isOpen ? null : slot); setSelectedRecipients([]); setSendNote(""); }}
                          className={`h-7 text-xs ${isOpen ? 'bg-blue-600 text-white border-blue-600' : 'border-blue-300 text-blue-700'}`}>
                          <Mail className="w-3.5 h-3.5 mr-1" />{isOpen ? 'Close' : 'Send'}
                        </Button>
                        <button onClick={() => handleDelete(slot)} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Upload zone */}
                {!doc && (
                  <div
                    onDrop={e => { e.preventDefault(); const arr = [...dragOver]; arr[slot] = false; setDragOver(arr); handleUpload(slot, e.dataTransfer.files?.[0]); }}
                    onDragOver={e => { e.preventDefault(); const arr = [...dragOver]; arr[slot] = true; setDragOver(arr); }}
                    onDragLeave={() => { const arr = [...dragOver]; arr[slot] = false; setDragOver(arr); }}
                    onClick={() => fileRefs.current[slot]?.click()}
                    className={`mx-4 my-3 border-2 border-dashed rounded-lg p-5 text-center cursor-pointer transition-colors ${isDragOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50/30'}`}
                  >
                    {isUploading
                      ? <div className="flex items-center justify-center gap-2"><Loader2 className="w-5 h-5 animate-spin text-blue-500" /><span className="text-sm text-blue-600">Uploading...</span></div>
                      : <><Upload className={`w-6 h-6 mx-auto mb-1.5 ${isDragOver ? 'text-blue-500' : 'text-gray-400'}`} /><p className="text-sm text-gray-500">Click to upload your own document</p><p className="text-xs text-gray-400 mt-0.5">PDF, JPG, PNG — Max 15MB</p></>
                    }
                  </div>
                )}

                {/* Built-in template card — placed inside its own slot */}
                {!doc && BUILT_IN_TEMPLATES[slot] && (() => {
                  const tmpl = BUILT_IN_TEMPLATES[slot];
                  return (
                    <div key={tmpl.key} className="mx-4 mb-3"
                      draggable
                      onDragStart={() => setTemplateDragKey(tmpl.key)}
                      onDragEnd={() => { setTemplateDragKey(null); setSlotTemplateOver(null); }}
                    >
                      <div className="flex items-start gap-3 p-4 rounded-xl border-2 cursor-grab active:cursor-grabbing select-none"
                        style={{ background: tmpl.bg, borderColor: tmpl.border }}>
                        <GripVertical className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: tmpl.color, opacity: 0.7 }} />
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm leading-tight" style={{ color: tmpl.color }}>{tmpl.label}</p>
                          <p className="text-xs text-gray-500 mt-1 leading-snug">{tmpl.description}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <p className="text-xs font-medium" style={{ color: tmpl.color }}>Drag into slot above →</p>
                            <button onClick={e => { e.stopPropagation(); setPreviewTemplate(tmpl.key); }}
                              className="text-xs px-2.5 py-1 rounded-full border font-medium hover:opacity-80"
                              style={{ color: tmpl.color, borderColor: tmpl.color, background: 'white' }}>View</button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {doc && (
                  <div className="mx-4 my-3 flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                    <FileText className="w-4 h-4 text-green-600 flex-shrink-0" />
                    <span className="text-sm text-green-700 flex-1 truncate">{doc.isTemplate ? doc.label : doc.fileName}</span>
                  </div>
                )}

                <input ref={el => fileRefs.current[slot] = el} type="file" accept=".pdf,.jpg,.jpeg,.png"
                  onChange={e => handleUpload(slot, e.target.files?.[0])} className="hidden" />

                {/* Send panel */}
                {isOpen && doc && (
                  <div className="mx-4 mb-4 bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-3">
                    <p className="text-sm font-semibold text-blue-800 flex items-center gap-2">
                      <Users className="w-4 h-4" />Select staff to receive acknowledgement link
                    </p>
                    <div className="space-y-1.5 max-h-40 overflow-y-auto">
                      {staffList.length === 0
                        ? <p className="text-xs text-gray-400 italic">No staff with email addresses found.</p>
                        : staffList.map(s => (
                          <label key={s.email} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg cursor-pointer border transition-all ${selectedRecipients.find(r => r.email === s.email) ? 'bg-blue-100 border-blue-400' : 'bg-white border-gray-200 hover:border-blue-300'}`}>
                            <input type="checkbox" checked={!!selectedRecipients.find(r => r.email === s.email)} onChange={() => toggleRecipient(s)} className="w-4 h-4 text-blue-600" />
                            <span className="text-sm font-medium flex-1">{s.name}</span>
                            <span className="text-xs text-gray-500">{s.email}</span>
                          </label>
                        ))
                      }
                    </div>
                    <div>
                      <Label className="text-xs text-blue-700 font-medium">Note to Staff (optional)</Label>
                      <textarea value={sendNote} onChange={e => setSendNote(e.target.value)}
                        placeholder="Add a note e.g. 'Please acknowledge before our Friday meeting.'"
                        rows={2} className="mt-1 w-full border border-blue-300 rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400 resize-none" />
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-gray-500">{selectedRecipients.length} selected</p>
                      <Button size="sm" onClick={() => handleSend(slot)} disabled={sending || selectedRecipients.length === 0} className="bg-blue-600 hover:bg-blue-700 text-white">
                        {sending ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Sending...</> : <><Mail className="w-4 h-4 mr-1" />Send Links</>}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Template Preview Modal — formal policy document style */}
        {previewTemplate && (() => {
          const content = POLICY_TEMPLATES[previewTemplate];
          const isDisclosure = previewTemplate === "disclosure_statement";
          const rc = (text) => text ? text.replace(/\[State Law Reference\]/g, disclosureStateCode.trim() || "[State Law Reference]") : text;
          return (
            <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setPreviewTemplate(null)}>
              <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
                {/* Modal header */}
                <div className="flex items-center justify-between px-5 py-3 bg-[#1a365d] text-white flex-shrink-0">
                  <p className="font-bold text-sm">Policy Document Preview</p>
                  <button onClick={() => setPreviewTemplate(null)} className="p-1.5 rounded-lg hover:bg-white/20"><X className="w-5 h-5" /></button>
                </div>
                {/* Document body */}
                <div className="overflow-y-auto flex-1 bg-gray-100 p-4">
                  <div className="bg-white shadow-sm rounded-lg max-w-xl mx-auto p-8" style={{ fontFamily: 'Georgia, serif' }}>
                    {/* Letterhead */}
                    <div style={{ borderBottom: '3px solid #1a365d', paddingBottom: 12, marginBottom: 20, textAlign: 'center' }}>
                      <p style={{ fontSize: 18, fontWeight: 700, color: '#1a365d', margin: 0 }}>{orgName || '[Organization Name]'}</p>
                      <p style={{ fontSize: 11, color: '#64748b', margin: '4px 0 0', fontStyle: 'italic' }}>Staff Policy Document</p>
                    </div>
                    {/* Title */}
                    <h1 style={{ fontSize: 15, fontWeight: 700, color: '#1a365d', marginBottom: 8, lineHeight: 1.4, textAlign: 'center' }}>{content?.title}</h1>
                    {/* Intro — for disclosure: centered lines + input box below last line */}
                    {content?.intro && (
                      isDisclosure ? (() => {
                        const lines = content.intro.split("\n");
                        const lastLine = lines[lines.length - 1].replace("[State Law Reference]", "").trim();
                        return (
                          <div style={{ textAlign: 'center', marginBottom: 16 }}>
                            {lines.slice(0, -1).map((line, i) => (
                              <p key={i} style={{ fontSize: 10, color: '#4a5568', fontStyle: 'italic', margin: 0, lineHeight: 1.3 }}>{line}</p>
                            ))}
                            <p style={{ fontSize: 10, color: '#4a5568', fontStyle: 'italic', margin: 0, lineHeight: 1.3 }}>{lastLine}</p>
                            <div style={{ marginTop: 10, display: 'flex', justifyContent: 'center' }}>
                              <input
                                value={disclosureStateCode}
                                onChange={e => setDisclosureStateCode(e.target.value)}
                                placeholder="Enter state law code"
                                style={{
                                  width: 280, padding: '6px 12px',
                                  border: '2px solid #7c3aed', borderRadius: 6, fontSize: 12,
                                  fontStyle: 'normal', fontFamily: 'system-ui, sans-serif',
                                  background: '#faf5ff', textAlign: 'center'
                                }}
                              />
                            </div>
                          </div>
                        );
                      })() : (
                        <p style={{ fontSize: 12, color: '#4a5568', fontStyle: 'italic', marginBottom: 16, lineHeight: 1.6 }}>{content.intro}</p>
                      )
                    )}
                    {/* Sections */}
                    {content?.sections?.map((sec, i) => (
                      <div key={i} style={{ marginBottom: 14 }}>
                        <p style={{ fontSize: 11, fontWeight: 700, color: '#1a365d', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6, borderBottom: '1px solid #e2e8f0', paddingBottom: 3 }}>{sec.heading}</p>
                        {sec.text && <p style={{ fontSize: 12, color: '#374151', lineHeight: 1.7, margin: '4px 0' }}>{rc(sec.text)}</p>}
                        {sec.items && <ul style={{ margin: '4px 0 4px 16px', padding: 0 }}>{sec.items.map((item, j) => (
                          <li key={j} style={{ fontSize: 12, color: '#374151', lineHeight: 1.7, marginBottom: 3 }}>{item}</li>
                        ))}</ul>}
                      </div>
                    ))}
                    {/* Acknowledgement */}
                    <div style={{ marginTop: 20, padding: '12px 16px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 4 }}>
                      <p style={{ fontSize: 11, fontStyle: 'italic', color: '#374151', lineHeight: 1.7, margin: 0 }}>{rc(content?.acknowledgementText)}</p>
                    </div>
                    {/* Signature line */}
                    <div style={{ marginTop: 24, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                      <div><div style={{ borderBottom: '1px solid #000', marginBottom: 4, height: 24 }} /><p style={{ fontSize: 10, color: '#64748b' }}>Staff Signature</p></div>
                      <div><div style={{ borderBottom: '1px solid #000', marginBottom: 4, height: 24 }} /><p style={{ fontSize: 10, color: '#64748b' }}>Date</p></div>
                    </div>
                  </div>
                </div>
                <div className="px-5 py-2.5 border-t bg-gray-50 flex-shrink-0 flex items-center justify-between gap-3">
                  <p className="text-xs text-gray-400">Drag this template into a policy slot above to send it to staff for digital signing.</p>
                  {isDisclosure && (
                    <Button size="sm" onClick={handleSaveStateCode} disabled={savingStateCode} className="bg-purple-700 hover:bg-purple-800 text-white flex-shrink-0">
                      {savingStateCode ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-3.5 h-3.5 mr-1" />Save Code</>}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

        {/* Acknowledgement Report */}
        <div className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden shadow-sm">
          <div className="flex items-center justify-between px-5 py-4 bg-gray-50 border-b border-gray-200">
            <div>
              <h3 className="font-bold text-gray-800 flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-600" />Acknowledgement Report
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">{acknowledged} acknowledged · {pending} pending</p>
            </div>
            <div className="flex gap-2">
              <select value={reportDocId || ''} onChange={e => { const v = e.target.value || null; setReportDocId(v); fetchReport(v); }}
                className="border border-gray-300 rounded-lg text-xs px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400">
                <option value="">All Policies</option>
                {docs.filter(Boolean).map(d => <option key={d.id} value={d.id}>{d.label || d.fileName}</option>)}
              </select>
              {acknowledged > 0 && (
                <Button size="sm" variant="outline" onClick={handleBackfillPdfs} disabled={backfillingPdfs}
                  className="h-8 border-amber-400 text-amber-700 hover:bg-amber-50"
                  title="Restore missing PDFs and compliance dates for older acknowledgements">
                  {backfillingPdfs
                    ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />Restoring…</>
                    : <><Download className="w-3.5 h-3.5 mr-1" />Restore Missing PDFs & Dates</>}
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={handlePrintReport} className="h-8">
                <Printer className="w-4 h-4 mr-1" />Print
              </Button>
            </div>
          </div>
          <div className="overflow-x-auto">
            {loadingReport
              ? <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
              : <table className="w-full text-xs">
                    <thead className="bg-blue-600 text-white">
                      <tr>
                        <th className="px-4 py-2.5 text-left">Policy</th>
                        <th className="px-4 py-2.5 text-left">Staff Member</th>
                        <th className="px-4 py-2.5 text-left">Sent</th>
                        <th className="px-4 py-2.5 text-left">Status</th>
                        <th className="px-4 py-2.5 text-left">Acknowledged On</th>
                        <th className="px-4 py-2.5 text-left">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reports.length === 0
                        ? <tr><td colSpan={6} className="text-sm text-gray-400 italic text-center py-8">No acknowledgements sent yet.</td></tr>
                        : reports.map((r, i) => (
                        <tr key={r.id || i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-4 py-2.5 font-medium">{r.policyName}</td>
                          <td className="px-4 py-2.5">{r.staffName}<br/><span className="text-gray-400">{r.staffEmail}</span></td>
                          <td className="px-4 py-2.5">{r.sentAt ? new Date(r.sentAt).toLocaleDateString() : '—'}</td>
                          <td className="px-4 py-2.5">
                            {r.acknowledged
                              ? <span className="flex items-center gap-1 text-green-600 font-semibold"><CheckCircle2 className="w-3.5 h-3.5" />Acknowledged</span>
                              : <span className="flex items-center gap-1 text-amber-600"><Clock className="w-3.5 h-3.5" />Pending</span>}
                          </td>
                          <td className="px-4 py-2.5">{r.acknowledgedAt ? new Date(r.acknowledgedAt).toLocaleDateString() : '—'}</td>
                          <td className="px-4 py-2.5">
                            <div className="flex items-center gap-1.5">
                              {!r.acknowledged && (
                                <button
                                  onClick={() => handleResendRecord(r.id)}
                                  disabled={resending === r.id}
                                  title="Resend reminder email"
                                  className="p-1.5 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded disabled:opacity-50">
                                  {resending === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                                </button>
                              )}
                              {r.acknowledged && r.receiptStoragePath && (
                                <button
                                  onClick={() => handleDownloadRecord(r.id, r.policyName, r.staffName)}
                                  title="Download signed policy PDF"
                                  className="p-1.5 text-green-600 hover:text-green-800 hover:bg-green-50 rounded">
                                  <Download className="w-3.5 h-3.5" />
                                </button>
                              )}
                              <button
                                onClick={() => handleDeleteRecord(r.id)}
                                title="Delete this record"
                                className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
            }
          </div>
        </div>
      </div>
    </AppShell>
  );
}
